import { isSignedToken } from './guestFlowGrant'

// Must match the backend (guest_flow_crypto.go / guest_flow_otp.go / guest_flow_block.go).
const JWE_ALG = 'dir'
const JWE_ENC = 'A256GCM'
const OTP_TOKEN_AUDIENCE = 'guest-otp'
const BLOCK_TOKEN_AUDIENCE = 'guest-block'

export const SPONSOR_STATUS_TAMPERED_MESSAGE =
  'We detected that the response from our server was modified on this device. For security reasons this request has been stopped.'

/**
 * Reads the JWE protected header (segment 0). The token's claims are encrypted
 * and can't be read here; the backend only copies values into the header that
 * the API response already shows in plain text (aud, guest_id, blocked). The
 * header is authenticated by AES-GCM, so the backend rejects the token if it
 * was edited — but the browser can't check that, so these are hints only.
 */
function decodeJweHeader(token) {
  try {
    const segment = token.split('.')[0]
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    return JSON.parse(window.atob(padded))
  } catch {
    return null
  }
}

function checkJweHeader(token, expectedAudience, name) {
  if (!isSignedToken(token)) {
    return { reason: `missing or malformed ${name}` }
  }
  const header = decodeJweHeader(token)
  if (!header || typeof header !== 'object') {
    return { reason: `${name} header is not readable` }
  }
  if (header.alg !== JWE_ALG || header.enc !== JWE_ENC) {
    return { reason: `${name} is not an encrypted guest-flow token` }
  }
  if (header.aud !== expectedAudience) {
    return { reason: `${name} audience mismatch` }
  }
  return { header }
}

/**
 * Checks a GET /api/guest/status/{id} response for signs that it was edited
 * in transit or in the browser (e.g. via a proxy / devtools override).
 *
 * Catches the edits needed to move the UI forward: "Approved" without a token,
 * a token for another guest request, a token on a non-approved status, or an
 * ID that doesn't match the request. The OTP endpoint decrypts and verifies
 * the token server-side, so a forged one is refused there regardless.
 *
 * @param {Object} params
 * @param {Object} params.response - parsed JSON body
 * @param {string|number} params.requestId - guest request ID that was polled
 * @returns {string|null} reason when tampering is detected, otherwise null
 */
export function detectSponsorStatusTampering({ response, requestId }) {
  if (!response || typeof response !== 'object' || Array.isArray(response)) {
    return 'response body is not an object'
  }

  const { id, status, otp_token: otpToken } = response

  if (id !== undefined && id !== null && String(id) !== String(requestId)) {
    return 'response id does not match the requested id'
  }

  if (status !== 'Approved') {
    if (otpToken !== undefined && otpToken !== null && otpToken !== '') {
      return 'otp_token present on a non-approved status'
    }
    return null
  }

  const { reason, header } = checkJweHeader(
    otpToken,
    OTP_TOKEN_AUDIENCE,
    'otp_token',
  )
  if (reason) return reason

  if (String(header.guest_id) !== String(requestId)) {
    return 'otp_token issued for a different guest request'
  }

  return null
}

/**
 * Checks a GET /api/guest/block response for signs that it was edited in
 * transit (e.g. flipping "message": true -> false with a proxy). The backend
 * copies its real decision into the token header as `blocked`; if it doesn't
 * match the plain `message` field, the response was tampered with.
 *
 * This is a UX signal only — the block is enforced server-side when the guest
 * request is submitted (POST /api/guest/submit returns 403).
 *
 * @param {Object} params
 * @param {Object} params.response - parsed JSON body
 * @returns {string|null} reason when tampering is detected, otherwise null
 */
export function detectBlockResponseTampering({ response }) {
  if (!response || typeof response !== 'object' || Array.isArray(response)) {
    return 'response body is not an object'
  }

  const { message, block_token: blockToken } = response

  if (typeof message !== 'boolean') {
    return 'block response message is not a boolean'
  }

  const { reason, header } = checkJweHeader(
    blockToken,
    BLOCK_TOKEN_AUDIENCE,
    'block_token',
  )
  if (reason) return reason

  if (header.blocked !== message) {
    return 'block response message does not match the block_token'
  }

  return null
}
